import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { FormattedMessage, useIntl, createIntl, RawIntlProvider } from 'react-intl'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { catalogFor } from '../i18n/catalog'
import { SOURCE_LOCALE } from '../i18n/config'
import { API_ENDPOINTS } from '../config/api'
import { sessionsAPI, setupAutoSave } from '../api/sessions'
import { setSessionOnlyToken } from '../api/client'
import QuestionRenderer from '../components/QuestionRenderer'
import ScoreBreakdown from '../components/ScoreBreakdown'
import TakeLanding from '../components/TakeLanding'
import ProfileStep from '../components/ProfileStep'
import ContextStep from '../components/ContextStep'
import type { ContextValue } from '../components/ContextStep'
import MyResultsHistory from '../components/results/MyResultsHistory'
import PasswordlessSignIn from '../components/PasswordlessSignIn'
import { useAuth } from '../contexts/AuthContext'

import ResultVisualization from '../components/results/ResultVisualization'
import type { GroupResult } from '../types'
import { getUIStrings } from '../i18n/ui'
import { resolveSurveyLanguage, getStoredSurveyLanguage, storeSurveyLanguage } from '../i18n/surveyLanguage'
import { resolveColor } from '../styles/colorContrast'

interface QuestionGroup {
  id: string
  name: string
  order: number
  color: string
  tint?: string
  description?: string
}

interface Question {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RATING' | 'COUNTRY'
  required: boolean
  order: number
  options: string[]
  group_id?: string
  weight?: number
  rating_labels?: Record<string, string>
  scoring_config?: {
    type: string
    min_value?: number
    max_value?: number
    true_value?: number
    false_value?: number
    option_weights?: Record<string, number>
  }
  original_options?: string[]
}

interface SubmitResult {
  score: number | null
  score_breakdown: Record<string, number> | null
  group_results?: GroupResult[]
  overall_percentage?: number
  result_type?: string
}

interface Survey {
  id: string
  title: string
  description: string
  organization_id?: string
  result_type?: string
  require_email?: boolean
  question_groups?: QuestionGroup[]
  questions: Question[]
  public_id?: string
  available_languages?: string[]
  default_language?: string
}

interface SavedProgress {
  responses: Record<string, any>
  currentIndex: number
  respondentEmail: string
  timeSpent: number
}

const STORAGE_KEY = (publicId: string) => `survey_progress_${publicId}`

const LANG_NAMES: Record<string, string> = {
  en: 'English', es: 'Espanol', fr: 'Francais', pt: 'Portugues',
  ar: 'العربية', sw: 'Kiswahili', hi: 'हिन्दी', ht: 'Kreyol',
  am: 'አማርኛ', so: 'Soomaali', zh: '中文', bn: 'বাংলা',
  de: 'Deutsch', ja: '日本語', ko: '한국어', ru: 'Русский',
  it: 'Italiano', nl: 'Nederlands', tr: 'Turkce', vi: 'Tieng Viet',
  lg: 'Luganda', hu: 'Magyar',
}

const UpgradeAccountPrompt: React.FC<{
  email: string
  onUpgraded: (token: string) => void
}> = ({ email, onUpgraded }) => {
  const intl = useIntl()
  const [password, setPassword] = useState('')
  const [upgradeError, setUpgradeError] = useState('')
  const [upgrading, setUpgrading] = useState(false)
  const [upgraded, setUpgraded] = useState(false)

  const handleUpgrade = async () => {
    if (password.length < 6) {
      setUpgradeError(intl.formatMessage({ id: 'take.passwordMinLength' }))
      return
    }
    setUpgrading(true)
    setUpgradeError('')
    try {
      const res = await fetch(API_ENDPOINTS.auth.upgrade, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.detail || intl.formatMessage({ id: 'take.upgradeFailed' }))
      }
      const data = await res.json()
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('session_token', data.session_token)
      setUpgraded(true)
      onUpgraded(data.access_token)
    } catch (err: any) {
      setUpgradeError(err.message || intl.formatMessage({ id: 'take.createAccountFailed' }))
    } finally {
      setUpgrading(false)
    }
  }

  if (upgraded) {
    return (
      <div className="bg-success-light border border-success rounded-lg p-5 mb-6 text-left">
        <h3 className="font-semibold text-foreground mb-1"><FormattedMessage id="take.accountCreated" /></h3>
        <p className="text-sm text-foreground-muted">
          <FormattedMessage id="take.accountCreatedBody" values={{ email: <strong>{email}</strong> }} />
        </p>
        <a
          href="/dashboard"
          className="inline-flex items-center mt-3 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-primary hover:bg-primary-dark"
        >
          <FormattedMessage id="take.goToDashboard" />
        </a>
      </div>
    )
  }

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-5 mb-6 text-left">
      <h3 className="font-semibold text-foreground mb-1"><FormattedMessage id="take.createAccountTitle" /></h3>
      <p className="text-sm text-foreground-muted mb-3">
        <FormattedMessage id="take.createAccountBody" values={{ email: <strong>{email}</strong> }} />
      </p>
      <div className="flex items-center gap-3">
        <input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setUpgradeError('') }}
          placeholder={intl.formatMessage({ id: 'take.choosePassword' })}
          className="border-2 border-border-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        />
        <button
          onClick={handleUpgrade}
          disabled={upgrading}
          className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-primary hover:bg-primary-dark disabled:opacity-50"
        >
          {upgrading ? <FormattedMessage id="take.creating" /> : <FormattedMessage id="take.createAccount" />}
        </button>
      </div>
      {upgradeError && <p className="mt-2 text-sm text-error">{upgradeError}</p>}
    </div>
  )
}

function getStoredAuthToken(): string | null {
  return localStorage.getItem('access_token')
}

function countAnswered(responses: Record<string, any>): number {
  return Object.values(responses).filter(
    (v) => v !== null && v !== undefined && v !== ''
  ).length
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

const PreviewBanner: React.FC<{ surveyId?: string }> = ({ surveyId }) => (
  <div className="bg-accent/20 border-b border-accent px-4 py-2 text-center text-sm">
    <span className="font-medium text-foreground"><FormattedMessage id="take.previewMode" /></span>
    <span className="text-foreground-muted"> <FormattedMessage id="take.previewModeNote" /> </span>
    {surveyId && (
      <Link to={`/surveys/${surveyId}/share`} className="text-primary hover:underline font-medium">
        <FormattedMessage id="take.backToSurvey" />
      </Link>
    )}
  </div>
)

/** Request 2: guest "save at the end" — passwordless sign-in then attach the response. */
const SaveResultPrompt: React.FC<{ responseId: string; onSaved: () => void }> = ({ responseId, onSaved }) => {
  const intl = useIntl()
  const { loginWithTokens } = useAuth()
  return (
    <div className="bg-surface rounded-xl border border-border-light p-6 mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-1"><FormattedMessage id="take.saveResultsTitle" /></h3>
      <p className="text-sm text-foreground-muted mb-4">
        <FormattedMessage id="take.saveResultsBody" />
      </p>
      <PasswordlessSignIn
        submitLabel={intl.formatMessage({ id: 'take.emailMeCode' })}
        claimResponseId={responseId}
        onSuccess={async (data) => {
          await loginWithTokens(data.access_token, data.session_token, data.user.email)
          try {
            await fetch(API_ENDPOINTS.surveys.claimResponse(responseId), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.access_token}` },
            })
          } catch { /* attaching is best-effort; the account still exists */ }
          onSaved()
        }}
      />
    </div>
  )
}

const SurveyTakePage: React.FC = () => {
  const { publicId = '' } = useParams<{ publicId: string }>()
  const [searchParams] = useSearchParams()
  const isPreview = searchParams.get('preview') === 'true'
  const [survey, setSurvey] = useState<Survey | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)
  const [responses, setResponses] = useState<Record<string, any>>({})
  const [respondentEmail, setRespondentEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [currentIndex, setCurrentIndex] = useState(-1)

  // Language state
  const [currentLang, setCurrentLang] = useState<string>('en')
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['en'])

  // On the respondent take/preview page, the chrome follows the SURVEY's
  // selected display language (the "Language" picker), not the admin's global
  // UI locale — a respondent who picks English should see everything in English.
  // We bind a local intl to currentLang and wrap every render branch in it.
  // (For a content-only language without a UI catalog, catalogFor falls back to
  // the source locale, so chrome stays English while content is translated.)
  const intl = useMemo(
    () => createIntl({
      locale: currentLang,
      defaultLocale: SOURCE_LOCALE,
      messages: catalogFor(currentLang),
      onError: () => {},
    }),
    [currentLang],
  )
  const wrapIntl = (node: React.ReactNode) => (
    <RawIntlProvider value={intl}>{node}</RawIntlProvider>
  )

  // Cooldown state
  const [cooldownDate, setCooldownDate] = useState<string | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  // Request 1/2: decoupled profile (geo) + guest/returning landing.
  const { user: authUser, isLoading: authLoading, loginWithTokens } = useAuth()
  const [takePhase, setTakePhase] = useState<'landing' | 'profile' | 'context' | 'signin' | 'history' | 'ready'>('ready')
  const [geo, setGeo] = useState<{ country: string; region: string } | null>(null)
  // Request 4: the respondent's "why now" note, attached to the response.
  const [surveyContext, setSurveyContext] = useState<ContextValue | null>(null)
  const [lastResponseId, setLastResponseId] = useState<string | null>(null)
  const [savedAtEnd, setSavedAtEnd] = useState(false)

  // Auto-enroll token (session-only, not persisted to localStorage)
  const [enrollToken, setEnrollToken] = useState<string | null>(null)
  const [enrolledEmail, setEnrolledEmail] = useState<string | null>(null)

  // Returns the best available auth token (stored > enroll)
  const getAuthToken = useCallback((): string | null => {
    return getStoredAuthToken() || enrollToken
  }, [enrollToken])

  // Session & auto-save state
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [savedProgress, setSavedProgress] = useState<SavedProgress | null>(null)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [timeSpent, setTimeSpent] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoSaveCleanupRef = useRef<(() => void) | null>(null)
  const responsesRef = useRef(responses)
  const currentIndexRef = useRef(currentIndex)
  const timeSpentRef = useRef(timeSpent)

  // Keep refs in sync
  useEffect(() => { responsesRef.current = responses }, [responses])
  useEffect(() => { currentIndexRef.current = currentIndex }, [currentIndex])
  useEffect(() => { timeSpentRef.current = timeSpent }, [timeSpent])

  // Load survey. Waits for AuthContext to settle so the language ladder sees
  // the signed-in user's locale on the very first resolution (rung 3) instead
  // of racing it.
  useEffect(() => {
    if (authLoading) return
    fetchSurvey()
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current)
      if (autoSaveCleanupRef.current) autoSaveCleanupRef.current()
    }
  }, [publicId, authLoading])

  const fetchSurvey = async (langOverride?: string) => {
    try {
      // Build URL with optional lang param
      let url = API_ENDPOINTS.surveys.public(publicId)
      const requestedLang = langOverride || searchParams.get('lang') || ''
      if (requestedLang) {
        url += `?lang=${encodeURIComponent(requestedLang)}`
      }

      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()
        const langs: string[] = data.available_languages || ['en']
        setAvailableLanguages(langs)

        // Full resolution ladder (spec: survey-language-detection):
        // ?lang > picker choice > user.locale > browser > survey default.
        // A picker click passes langOverride and skips re-resolution.
        if (!langOverride) {
          const resolved = resolveSurveyLanguage({
            urlLang: searchParams.get('lang'),
            pickerChoice: publicId ? getStoredSurveyLanguage(publicId) : null,
            userLocale: authUser?.locale,
            browserLang: navigator.language,
            availableLanguages: langs,
            defaultLanguage: data.default_language || 'en',
            allowPseudo: import.meta.env.DEV,
          })
          const fetchedLang = requestedLang || data.default_language || 'en'
          if (resolved !== fetchedLang) {
            // Re-fetch so content and chrome land in the resolved language
            return fetchSurvey(resolved)
          }
        }

        setCurrentLang(requestedLang || data.default_language || 'en')
        setSurvey(data)
        const initialResponses: Record<string, any> = {}
        data.questions?.forEach((q: Question) => {
          if (q.type === 'BOOLEAN') initialResponses[q.id] = null
          else if (q.type === 'RATING') initialResponses[q.id] = null
          else if (q.type === 'COUNTRY') initialResponses[q.id] = null
          else initialResponses[q.id] = ''
        })
        setResponses(initialResponses)

        // Check cooldown for logged-in users
        const token = getAuthToken()
        if (token) {
          setIsLoggedIn(true)
          try {
            const canRes = await fetch(API_ENDPOINTS.surveys.canAttempt(data.id), {
              headers: { 'Authorization': `Bearer ${token}` },
            })
            if (canRes.ok) {
              const canData = await canRes.json()
              if (!canData.can_attempt && canData.next_eligible_date) {
                setCooldownDate(canData.next_eligible_date)
              }
            }
          } catch (err) {
            console.error('Failed to check cooldown:', err)
          }
        }

        // Check for existing progress
        await checkForSavedProgress(data, initialResponses)
      } else if (response.status === 404) {
        setError(intl.formatMessage({ id: 'take.surveyNotFound' }))
      } else {
        setError(intl.formatMessage({ id: 'take.failedToLoad' }))
      }
    } catch (err) {
      console.error('Error fetching survey:', err)
      setError(intl.formatMessage({ id: 'take.failedToLoad' }))
    } finally {
      setLoading(false)
    }
  }

  const checkForSavedProgress = async (surveyData: Survey, initialResponses: Record<string, any>) => {
    const token = getAuthToken()

    if (token && !isPreview) {
      // Logged-in: try database session, always check localStorage as fallback
      try {
        const session = await sessionsAPI.startSession(surveyData.id)
        setSessionId(session.id)
        if (session.status === 'in_progress' && countAnswered(session.responses || {}) > 0) {
          const merged = { ...initialResponses, ...session.responses }
          setSavedProgress({
            responses: merged,
            currentIndex: session.current_question_index || 0,
            respondentEmail: '',
            timeSpent: session.time_spent_seconds || 0,
          })
        } else {
          // DB session has no progress, check localStorage
          checkLocalStorage(initialResponses)
        }
      } catch (_err) {
        console.log('Session API unavailable, using localStorage fallback')
        checkLocalStorage(initialResponses)
      }
    } else {
      // Anonymous: check localStorage
      checkLocalStorage(initialResponses)
    }
  }

  const checkLocalStorage = (initialResponses: Record<string, any>) => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY(publicId))
      if (saved) {
        const parsed: SavedProgress = JSON.parse(saved)
        if (countAnswered(parsed.responses) > 0) {
          const merged = { ...initialResponses, ...parsed.responses }
          setSavedProgress({ ...parsed, responses: merged })
        }
      }
    } catch (_err) {
      console.log('No saved progress in localStorage')
    }
  }

  // Request 2: first-timers (not logged in, no saved progress) start at the
  // guest/returning landing; logged-in and resuming users keep the normal flow.
  // Checks the token directly (not the async isLoggedIn state) so a signed-in
  // user never flashes the guest landing while login state is still committing.
  useEffect(() => {
    if (loading || isPreview || authLoading) return
    if (!isLoggedIn && !getAuthToken() && !savedProgress && currentIndex === -1) {
      setTakePhase((p) => (p === 'ready' ? 'landing' : p))
    }
  }, [loading, isLoggedIn, isPreview, savedProgress, currentIndex, authLoading, getAuthToken])

  // Start timer when questions begin
  useEffect(() => {
    if (currentIndex >= 0 && !submitted) {
      timerRef.current = setInterval(() => {
        setTimeSpent((prev) => prev + 1)
      }, 1000)

      // Start auto-save
      if (sessionId) {
        startDatabaseAutoSave()
      }

      return () => {
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }
  }, [currentIndex >= 0, sessionId, submitted])

  const startDatabaseAutoSave = () => {
    if (autoSaveCleanupRef.current) autoSaveCleanupRef.current()
    if (!sessionId || isPreview) return

    autoSaveCleanupRef.current = setupAutoSave(sessionId, () => {
      setAutoSaveStatus('saving')
      return {
        responses: responsesRef.current,
        current_question_index: currentIndexRef.current,
        time_spent_seconds: timeSpentRef.current,
        progress_percentage: Math.round(
          (countAnswered(responsesRef.current) / (survey?.questions?.length || 1)) * 100
        ),
      }
    }, 5000)
  }

  // localStorage auto-save on every response change (always, as safety net)
  useEffect(() => {
    if (currentIndex >= 0 && !submitted) {
      saveToLocalStorage()
    }
  }, [responses, currentIndex])

  const saveToLocalStorage = () => {
    try {
      const progress: SavedProgress = {
        responses: responsesRef.current,
        currentIndex: currentIndexRef.current,
        respondentEmail,
        timeSpent: timeSpentRef.current,
      }
      localStorage.setItem(STORAGE_KEY(publicId), JSON.stringify(progress))
      setAutoSaveStatus('saved')
      setTimeout(() => setAutoSaveStatus('idle'), 2000)
    } catch (err) {
      console.error('localStorage save failed:', err)
    }
  }

  // Auto-save status callback (for database saves)
  useEffect(() => {
    if (autoSaveStatus === 'saving') {
      const timer = setTimeout(() => setAutoSaveStatus('saved'), 500)
      return () => clearTimeout(timer)
    }
    if (autoSaveStatus === 'saved') {
      const timer = setTimeout(() => setAutoSaveStatus('idle'), 2000)
      return () => clearTimeout(timer)
    }
  }, [autoSaveStatus])

  const handleResume = () => {
    if (!savedProgress) return
    setResponses(savedProgress.responses)
    setCurrentIndex(savedProgress.currentIndex)
    setRespondentEmail(savedProgress.respondentEmail || '')
    setTimeSpent(savedProgress.timeSpent || 0)
    setSavedProgress(null)
  }

  const handleStartOver = () => {
    setSavedProgress(null)
    localStorage.removeItem(STORAGE_KEY(publicId))
    if (sessionId) {
      sessionsAPI.abandonSession(sessionId).catch(() => {})
      // Start a fresh session
      if (survey) {
        sessionsAPI.startSession(survey.id).then((s) => setSessionId(s.id)).catch(() => {})
      }
    }
    setCurrentIndex(0)
    setTimeSpent(0)
  }

  const switchLanguage = async (lang: string) => {
    if (lang === currentLang) return
    // The picker choice persists per survey and beats account/browser
    // preferences on the next visit (rung 2 of the resolution ladder).
    if (publicId) storeSurveyLanguage(publicId, lang)
    setLoading(true)
    await fetchSurvey(lang)
    setLoading(false)
  }

  // Request 1: geo is a decoupled, org-configured profile field, captured in its
  // own step, never as a survey question. The org's profile_config drives whether
  // we collect it; any stray COUNTRY question is filtered out of the flow.
  const allQuestions = survey?.questions || []
  const wantsGeo = !!(survey as any)?.profile_config?.geo?.enabled
  const questions = allQuestions.filter((q) => q.type !== 'COUNTRY')
  const totalQuestions = questions.length

  // Compute pages: grouped surveys show one group per page, flat surveys show one question per page
  const isGrouped = !!(survey?.question_groups && survey.question_groups.length > 0)
  const pages: Array<{ group: QuestionGroup | null; questions: Question[] }> = (() => {
    if (!isGrouped) {
      return questions.map(q => ({ group: null, questions: [q] }))
    }
    const sorted = [...survey!.question_groups!].sort((a, b) => a.order - b.order)
    const result: Array<{ group: QuestionGroup | null; questions: Question[] }> = []
    for (const g of sorted) {
      const gq = questions.filter(q => q.group_id === g.id).sort((a, b) => a.order - b.order)
      if (gq.length > 0) result.push({ group: g, questions: gq })
    }
    const ungrouped = questions.filter(q => !q.group_id)
    for (const q of ungrouped) result.push({ group: null, questions: [q] })
    return result
  })()

  const totalPages = pages.length
  const currentPage = currentIndex >= 0 && currentIndex < totalPages ? pages[currentIndex] : null
  const isLastPage = currentIndex === totalPages - 1

  const canAdvance = useCallback(() => {
    if (!currentPage) return true
    return currentPage.questions.every(q => {
      if (!q.required) return true
      const val = responses[q.id]
      return val !== null && val !== undefined && val !== ''
    })
  }, [currentPage, responses])

  const handleNext = () => {
    if (isLastPage) {
      handleSubmit()
    } else {
      setCurrentIndex((prev) => prev + 1)
      window.scrollTo(0, 0)
      setError('')
    }
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(-1, prev - 1))
    window.scrollTo(0, 0)
    setError('')
  }

  const handleResponseChange = (questionId: string, value: any) => {
    // For translated surveys, map selected option back to original English value
    const question = questions.find(q => q.id === questionId)
    if (question?.original_options && question.options) {
      if (Array.isArray(value)) {
        // MULTIPLE_CHOICE: map each selected item back to English
        value = value.map((v: string) => {
          const idx = question.options.indexOf(v)
          return idx >= 0 && question.original_options![idx] ? question.original_options![idx] : v
        })
      } else if (typeof value === 'string') {
        // SINGLE_CHOICE: map single selection back to English
        const idx = question.options.indexOf(value)
        if (idx >= 0 && question.original_options[idx]) {
          value = question.original_options[idx]
        }
      }
    }
    setResponses((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleSubmit = async () => {
    if (isPreview) {
      setSubmitted(true)
      return
    }

    for (const q of questions) {
      const val = responses[q.id]
      if (q.required && (val === null || val === undefined || val === '')) {
        setError(intl.formatMessage({ id: 'take.pleaseAnswer' }, { question: q.text }))
        return
      }
    }

    setSubmitting(true)
    setError('')

    try {
      if (sessionId) {
        // Complete the session
        await sessionsAPI.completeSession(sessionId, responses)
      }

      // Always submit via the survey respond endpoint to get scores back
      const token = getAuthToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      const response = await fetch(API_ENDPOINTS.surveys.respond(publicId), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          respondent_email: respondentEmail || undefined,
          responses,
          geo: geo || undefined,
          context: surveyContext || undefined,
        }),
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || intl.formatMessage({ id: 'take.failedToSubmit' }))
      }
      const result = await response.json()
      if (result.id) setLastResponseId(result.id)
      if (result.score !== null && result.score !== undefined || result.group_results) {
        setSubmitResult({
          score: result.score,
          score_breakdown: result.score_breakdown,
          group_results: result.group_results,
          overall_percentage: result.overall_percentage,
          result_type: survey?.result_type,
        })
      }

      // Cleanup
      localStorage.removeItem(STORAGE_KEY(publicId))
      if (autoSaveCleanupRef.current) autoSaveCleanupRef.current()
      if (timerRef.current) clearInterval(timerRef.current)
      setSubmitted(true)
    } catch (err: any) {
      console.error('Error submitting response:', err)
      setError(err.message || intl.formatMessage({ id: 'take.failedToSubmit' }))
    } finally {
      setSubmitting(false)
    }
  }

  // --- Loading ---
  if (loading) {
    return wrapIntl(
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted"><FormattedMessage id="take.loadingSurvey" /></p>
        </div>
      </div>
    )
  }

  // --- Error (no survey) ---
  if (error && !survey) {
    return wrapIntl(
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-4">{error}</p>
          <a href="/" className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark">
            <FormattedMessage id="take.backToHome" />
          </a>
        </div>
      </div>
    )
  }

  // --- Thank You / Results ---
  if (submitted && isPreview) {
    return wrapIntl(
      <div className="min-h-screen bg-background">
        <PreviewBanner surveyId={survey?.id} />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-surface rounded-xl shadow-sm border border-border-light p-8">
            <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👁</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2"><FormattedMessage id="take.previewComplete" /></h2>
            <p className="text-foreground-muted mb-6">
              <FormattedMessage id="take.previewCompleteBody" />
            </p>
            {survey?.id && (
              <Link
                to={`/surveys/${survey.id}/share`}
                className="inline-flex items-center px-6 py-2 text-sm font-semibold rounded-lg text-white bg-primary hover:bg-primary-dark"
              >
                <FormattedMessage id="take.backToSurvey" />
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (submitted) {
    const tUI = getUIStrings(currentLang)
    // Show group result visualization for structured surveys
    if (submitResult && submitResult.group_results && submitResult.group_results.length > 0 && survey) {
      return wrapIntl(
        <div className="min-h-screen bg-background">
          <div className="bg-surface shadow-sm">
            <div className="max-w-2xl mx-auto px-4 py-4">
              <h1 className="text-lg font-bold text-foreground">{tUI.assessmentComplete}</h1>
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-surface rounded-xl shadow-sm border border-border-light p-8 mb-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-success-light rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{tUI.thankYou}</h2>
                  <p className="text-foreground-muted text-sm">{tUI.resultsForTitle(survey.title)}</p>
                </div>
              </div>
              <ResultVisualization
                resultType={submitResult.result_type || survey.result_type || 'flat'}
                groupResults={submitResult.group_results}
                overallPercentage={submitResult.overall_percentage}
                score={submitResult.score ?? undefined}
                scoreBreakdown={submitResult.score_breakdown ?? undefined}
                lang={currentLang}
              />
            </div>
            {!isLoggedIn && enrolledEmail && (
              <UpgradeAccountPrompt
                email={enrolledEmail}
                onUpgraded={() => { setIsLoggedIn(true); setEnrollToken(null); setSessionOnlyToken(null) }}
              />
            )}
            {!isLoggedIn && !enrolledEmail && !savedAtEnd && lastResponseId && (
              <SaveResultPrompt responseId={lastResponseId} onSaved={() => { setSavedAtEnd(true); setIsLoggedIn(true) }} />
            )}
            {savedAtEnd && (
              <p className="text-center text-success font-medium mb-6"><FormattedMessage id="take.resultsSaved" /></p>
            )}
            <div className="text-center">
              <a href="/" className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark">
                {tUI.backToHome}
              </a>
            </div>
          </div>
        </div>
      )
    }

    // Show flat score breakdown if scores are available
    if (submitResult && submitResult.score !== null && submitResult.score_breakdown && survey) {
      return wrapIntl(
        <div className="min-h-screen bg-background">
          <div className="bg-surface shadow-sm">
            <div className="max-w-2xl mx-auto px-4 py-4">
              <h1 className="text-lg font-bold text-foreground">{tUI.surveyComplete}</h1>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-surface rounded-xl shadow-sm border border-border-light p-8 mb-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-success-light rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{tUI.thankYou}</h2>
                  <p className="text-foreground-muted text-sm">{tUI.scoredOnTitle(survey.title)}</p>
                </div>
              </div>
              <ScoreBreakdown
                score={submitResult.score ?? undefined}
                scoreBreakdown={submitResult.score_breakdown ?? undefined}
                questions={questions}
                responses={responses}
              />
            </div>
            {!isLoggedIn && enrolledEmail && (
              <UpgradeAccountPrompt
                email={enrolledEmail}
                onUpgraded={() => { setIsLoggedIn(true); setEnrollToken(null); setSessionOnlyToken(null) }}
              />
            )}
            {!isLoggedIn && !enrolledEmail && !savedAtEnd && lastResponseId && (
              <SaveResultPrompt responseId={lastResponseId} onSaved={() => { setSavedAtEnd(true); setIsLoggedIn(true) }} />
            )}
            {savedAtEnd && (
              <p className="text-center text-success font-medium mb-6"><FormattedMessage id="take.resultsSaved" /></p>
            )}
            <div className="text-center">
              <a href="/" className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark">
                {tUI.backToHome}
              </a>
            </div>
          </div>
        </div>
      )
    }

    // Plain thank-you for unscored surveys
    return wrapIntl(
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-success-light rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-3">{tUI.thankYou}</h2>
          <p className="text-foreground-muted mb-8">{tUI.responseRecorded}</p>
          {!isLoggedIn && enrolledEmail && (
              <UpgradeAccountPrompt
                email={enrolledEmail}
                onUpgraded={() => { setIsLoggedIn(true); setEnrollToken(null); setSessionOnlyToken(null) }}
              />
            )}
          <a href="/" className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark">
            {tUI.backToHome}
          </a>
        </div>
      </div>
    )
  }

  // --- Intro / Email Screen ---
  const emailRequired = survey?.require_email !== false
  const sortedIntroGroups = isGrouped
    ? [...(survey?.question_groups || [])].sort((a, b) => a.order - b.order)
    : []
  const estimatedMinutes = Math.max(1, Math.ceil(totalQuestions * 0.3))

  const handleStartSurvey = async () => {
    if (emailRequired && !isPreview) {
      if (!respondentEmail.trim()) {
        setEmailError(intl.formatMessage({ id: 'take.emailRequired' }))
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respondentEmail)) {
        setEmailError(intl.formatMessage({ id: 'take.emailInvalid' }))
        return
      }
    }
    setEmailError('')

    // Auto-enroll respondent if they provided an email and are not already logged in
    if (respondentEmail && !getStoredAuthToken() && survey?.organization_id) {
      try {
        const enrollRes = await fetch(API_ENDPOINTS.auth.autoEnroll, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: respondentEmail,
            survey_id: publicId,
            organization_id: survey.organization_id,
          }),
        })
        if (enrollRes.ok) {
          const enrollData = await enrollRes.json()
          setEnrollToken(enrollData.token)
          setEnrolledEmail(respondentEmail)
          // Make the token available to apiClient for session/auto-save calls
          setSessionOnlyToken(enrollData.token)

          // Start a server-side session using the enroll token
          try {
            const session = await sessionsAPI.startSession(survey.id)
            setSessionId(session.id)
          } catch (err) {
            console.log('Could not start server session for enrolled respondent:', err)
          }
        }
      } catch (err) {
        console.log('Auto-enroll failed, continuing without:', err)
      }
    }

    setCurrentIndex(0)
  }

  // Request 2: guest-vs-returning landing, and request 1: the decoupled geo step.
  if (takePhase !== 'ready') {
    return wrapIntl(
      <div className="min-h-screen bg-background py-12 px-4">
        <div className={`mx-auto bg-surface rounded-2xl shadow-sm p-8 ${takePhase === 'history' ? 'max-w-3xl' : 'max-w-md'}`}>
          {takePhase === 'landing' && (
            <TakeLanding
              surveyTitle={survey?.title || ''}
              onTakeAsGuest={() => {
                if (wantsGeo) setTakePhase('profile')
                else setTakePhase('context')
              }}
              onSignIn={() => setTakePhase('signin')}
            />
          )}
          {takePhase === 'profile' && (
            <ProfileStep onComplete={(p) => { setGeo(p); setTakePhase('context') }} />
          )}
          {takePhase === 'context' && (
            <ContextStep
              onComplete={(c) => { setSurveyContext(c); setTakePhase('ready'); setCurrentIndex(0) }}
              onSkip={() => { setSurveyContext(null); setTakePhase('ready'); setCurrentIndex(0) }}
            />
          )}
          {takePhase === 'signin' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-foreground text-center"><FormattedMessage id="take.welcomeBack" /></h2>
              <PasswordlessSignIn
                onSuccess={async (data) => {
                  await loginWithTokens(data.access_token, data.session_token, data.user.email)
                  setIsLoggedIn(true)
                  setRespondentEmail(data.user.email)
                  setTakePhase('history')
                }}
              />
              <button type="button" onClick={() => setTakePhase('landing')} className="w-full text-sm text-foreground-muted">
                <FormattedMessage id="take.back" />
              </button>
            </div>
          )}
          {takePhase === 'history' && (
            <div className="space-y-6">
              <MyResultsHistory surveyId={publicId} />
              <button
                type="button"
                onClick={() => { if (wantsGeo && !geo) setTakePhase('profile'); else setTakePhase('context') }}
                className="w-full bg-primary text-white font-semibold py-3 rounded-lg"
              >
                <FormattedMessage id="take.takeTestAgain" />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (currentIndex === -1) {
    return wrapIntl(
      <div className="min-h-screen bg-background">
        {isPreview && <PreviewBanner surveyId={survey?.id} />}
        <div className="bg-surface shadow-sm">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <h1 className="text-lg font-bold text-foreground"><FormattedMessage id="take.impactpulseSurvey" /></h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold text-foreground mb-3">{survey?.title}</h2>
          {survey?.description && (
            <p className="text-foreground-muted mb-6 text-lg">{survey.description}</p>
          )}

          {/* Resume card */}
          {savedProgress && !isPreview && (
            <div className="mb-6 bg-primary/10 border border-primary-light rounded-lg p-5">
              <h3 className="font-semibold text-foreground mb-1"><FormattedMessage id="take.resumeTitle" /></h3>
              <p className="text-sm text-foreground-muted mb-4">
                <FormattedMessage id="take.questionsAnswered" values={{ answered: countAnswered(savedProgress.responses), total: totalQuestions }} />
                {savedProgress.timeSpent > 0 && <> &middot; <FormattedMessage id="take.timeSpent" values={{ time: formatTime(savedProgress.timeSpent) }} /></>}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleResume}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                >
                  <FormattedMessage id="take.resume" />
                </button>
                <button
                  onClick={handleStartOver}
                  className="px-5 py-2.5 text-sm font-medium text-foreground bg-surface border border-border hover:bg-background rounded-lg transition-colors"
                >
                  <FormattedMessage id="take.startOver" />
                </button>
              </div>
            </div>
          )}

          {!savedProgress && (
            <>
              {/* Language picker */}
              {availableLanguages.length > 1 && (
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-sm text-foreground-muted"><FormattedMessage id="take.languageLabel" /></span>
                  <div className="flex flex-wrap gap-1.5">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang}
                        onClick={() => switchLanguage(lang)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          currentLang === lang
                            ? 'bg-primary text-white border-primary'
                            : 'bg-surface text-foreground border-border hover:border-primary'
                        }`}
                      >
                        {LANG_NAMES[lang] || lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Before You Begin card */}
              <div className="bg-surface rounded-xl shadow-sm border border-border-light p-6 mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4"><FormattedMessage id="take.beforeYouBegin" /></h3>
                {!isPreview && (
                <div className="mb-4">
                  <label htmlFor="email" className="flex items-center gap-1 text-sm font-medium text-foreground mb-1">
                    <FormattedMessage id="take.emailAddress" />
                    {emailRequired && <span className="text-xs text-foreground-muted"><FormattedMessage id="take.requiredMarker" /></span>}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={respondentEmail}
                    onChange={(e) => { setRespondentEmail(e.target.value); setEmailError('') }}
                    className="block w-full border-2 border-border-light rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="your@email.com"
                  />
                  {emailError && <p className="mt-1 text-sm" style={{ color: '#ef4444' }}>{emailError}</p>}
                </div>
                )}
                <div className="border-t border-border-light pt-3">
                  <div className="flex gap-6">
                    <div>
                      <span className="text-sm font-bold text-foreground"><FormattedMessage id="take.questionsCount" values={{ count: totalQuestions }} /></span>
                      <p className="text-xs text-foreground-muted">{sortedIntroGroups.length > 0 ? intl.formatMessage({ id: 'take.sectionsCount' }, { count: sortedIntroGroups.length }) : intl.formatMessage({ id: 'take.surveyWord' })}</p>
                    </div>
                    <div>
                      <span className="text-sm font-bold text-foreground"><FormattedMessage id="take.minutesEstimate" values={{ minutes: estimatedMinutes }} /></span>
                      <p className="text-xs text-foreground-muted"><FormattedMessage id="take.estimatedTime" /></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section list */}
              {sortedIntroGroups.length > 0 && (
                <div className="bg-surface rounded-xl shadow-sm border border-border-light p-6 mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3"><FormattedMessage id="take.surveySections" /></h3>
                  <div className="flex flex-col gap-2">
                    {sortedIntroGroups.map((g, i) => {
                      const count = questions.filter((q) => q.group_id === g.id).length
                      return (
                        <div key={g.id} className="flex items-center gap-3 py-1">
                          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: g.color }} />
                          <span className="text-sm font-bold text-foreground">{i + 1}. {g.name}</span>
                          <span className="text-xs text-foreground-muted"><FormattedMessage id="take.questionsParen" values={{ count }} /></span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {cooldownDate ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
                  <h3 className="font-semibold text-yellow-800 mb-1"><FormattedMessage id="take.notYetEligible" /></h3>
                  <p className="text-sm text-yellow-700">
                    <FormattedMessage
                      id="take.retakeAfter"
                      values={{ date: <strong>{new Date(cooldownDate).toLocaleDateString()}</strong> }}
                    />
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleStartSurvey}
                  className="w-full py-4 px-6 text-lg font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                >
                  <FormattedMessage id="take.beginSurvey" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const currentGroup = currentPage?.group || null
  const currentGroupColor = currentGroup?.color || null
  // Resolve to a safe hex for plain fills (bar/dots/swatch). Named colors like
  // "yellow" become a readable amber instead of #FFFF00.
  const currentGroupHex = currentGroupColor ? resolveColor(currentGroupColor) : null
  const answeredOnPage = currentPage
    ? currentPage.questions.filter(q => {
        const v = responses[q.id]; return v !== null && v !== undefined && v !== ''
      }).length
    : 0
  const overallProgress = totalQuestions > 0
    ? (countAnswered(responses) / totalQuestions) * 100
    : 0

  // --- Question Screen ---
  return wrapIntl(
    <div className="min-h-screen bg-background flex flex-col">
      {isPreview && <PreviewBanner surveyId={survey?.id} />}
      {/* Progress bar */}
      <div className="bg-surface shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-foreground-muted">
              {isGrouped && currentGroup
                ? <FormattedMessage id="take.sectionProgress" values={{ current: currentIndex + 1, total: totalPages }} />
                : <FormattedMessage id="take.questionProgress" values={{ current: currentIndex + 1, total: totalPages }} />
              }
            </span>
            <div className="flex items-center gap-3">
              {autoSaveStatus === 'saving' && (
                <span className="flex items-center text-xs text-foreground-muted">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-foreground-muted mr-1" />
                  <FormattedMessage id="take.saving" />
                </span>
              )}
              {autoSaveStatus === 'saved' && (
                <span className="flex items-center text-xs text-primary">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <FormattedMessage id="take.autoSaved" />
                </span>
              )}
              <span className="text-sm text-foreground-muted">{Math.round(overallProgress)}%</span>
            </div>
          </div>
          {/* Overall progress bar */}
          <div className="w-full bg-border-light rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{
                width: `${overallProgress}%`,
                backgroundColor: currentGroupHex || 'var(--color-primary, #6366F1)',
              }}
            />
          </div>
          {/* Group dots */}
          {isGrouped && pages.length > 1 && (
            <div className="flex gap-1 mt-2 justify-center">
              {pages.map((page, i) => (
                <div
                  key={i}
                  className="h-2 rounded-sm"
                  style={{
                    width: `${100 / pages.length}%`,
                    backgroundColor: i <= currentIndex
                      ? (page.group?.color ? resolveColor(page.group.color) : 'var(--color-primary, #6366F1)')
                      : 'var(--theme-secondary, #e5e7eb)',
                    opacity: i === currentIndex ? 1 : 0.5,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8 w-full">
          {/* Group header */}
          {currentGroup && (
            <div className="bg-surface rounded-xl shadow-sm border border-border-light p-5 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: currentGroupHex || '#6B7280' }} />
                <h2 className="text-xl font-bold text-foreground">{currentGroup.name}</h2>
              </div>
              {currentGroup.description && (
                <p className="text-sm text-foreground-muted mt-1 ml-7">{currentGroup.description}</p>
              )}
              <p className="text-xs text-foreground-muted mt-2 ml-7">
                <FormattedMessage id="take.answeredCount" values={{ answered: answeredOnPage, total: currentPage!.questions.length }} />
              </p>
            </div>
          )}

          {/* Questions on this page */}
          <div className="space-y-4">
            {currentPage?.questions.map((q) => (
              <div key={q.id} className="bg-surface rounded-xl shadow-sm border border-border-light p-6">
                <div className="mb-4">
                  {!isGrouped && (
                    <p className="text-sm font-medium text-primary mb-2">
                      <FormattedMessage id="take.questionNumber" values={{ number: currentIndex + 1 }} />
                    </p>
                  )}
                  <h3 className={`${isGrouped ? 'text-lg' : 'text-2xl'} font-bold text-foreground`}>
                    {q.text}
                    {q.required && <span className="text-error ml-1">*</span>}
                  </h3>
                </div>
                <QuestionRenderer
                  question={q}
                  value={responses[q.id]}
                  onChange={(value) => handleResponseChange(q.id, value)}
                  ratingLabels={q.rating_labels}
                  groupColor={currentGroupColor || undefined}
                />
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 bg-error-light border border-error text-error px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              type="button"
              onClick={handlePrevious}
              className={`flex items-center px-5 py-3 text-sm font-medium rounded-lg transition-colors
                ${currentIndex === 0
                  ? 'text-foreground-muted cursor-not-allowed'
                  : 'text-foreground hover:bg-surface hover:shadow-sm border border-transparent hover:border-border-light'
                }
              `}
              disabled={currentIndex === 0}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {isGrouped ? <FormattedMessage id="take.previousSection" /> : <FormattedMessage id="take.previous" />}
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance() || submitting}
              className={`flex items-center px-6 py-3 text-sm font-semibold rounded-lg transition-colors
                ${!canAdvance() || submitting
                  ? 'bg-border-light text-foreground-muted cursor-not-allowed'
                  : isLastPage
                    ? 'bg-primary text-white hover:bg-primary-dark shadow-md'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }
              `}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  <FormattedMessage id="take.submitting" />
                </>
              ) : isLastPage ? (
                <>
                  <FormattedMessage id="take.submitSurvey" />
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              ) : (
                <>
                  {isGrouped ? <FormattedMessage id="take.nextSection" /> : <FormattedMessage id="take.next" />}
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SurveyTakePage
